import axios, { AxiosResponse } from 'axios'
import {
  FrameModel,
  ShellModel,
  AnalysisResult,
  ApiResponse,
  ExampleData
} from '@structura/types'
import { getSessionId } from './sessionUtils'

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor -- attach session identity + optional JWT to every request
api.interceptors.request.use(
  (config) => {
    config.headers['X-Session-Id'] = getSessionId()

    // Tell the backend which UI language to localize responses (e.g. error messages) in.
    try {
      config.headers['Accept-Language'] = localStorage.getItem('structura.language') || 'nb'
    } catch {
      // ignore – localStorage may be unavailable
    }

    try {
      const token = localStorage.getItem('structura.auth.token') || ''
      if (token.length > 0) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
    } catch {
      // ignore – localStorage may be unavailable
    }

    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

// Analysis API
export const analysisApi = {
  // Frame Analysis
  analyzeFrame: async (model: FrameModel): Promise<AnalysisResult> => {
    try {
      console.log('Sending frame analysis request:', model)
      const response = await api.post<ApiResponse<any>>('/FrameAnalysis/analyze', model)
      console.log('Received frame response:', response.data)
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message ?? 'Frame analysis completed successfully',
          data: response.data.data
        }
      } else {
        return {
          success: false,
          message: `[Frontend API] ${response.data.message ?? 'Frame analysis failed'}`
        }
      }
    } catch (error: any) {
      console.error('Frame analysis error:', error)
      return { 
        success: false, 
        message: `[Frontend API Error] ${error.response?.data?.message ?? error.message ?? 'Frame analysis failed'}` 
      }
    }
  },

  // Shell Analysis
  analyzeShell: async (model: ShellModel): Promise<AnalysisResult> => {
    try {
      console.log('Sending shell analysis request:', model)
      const response = await api.post<ApiResponse<any>>('/ShellAnalysis/analyze', model)
      console.log('Received shell response:', response.data)
      
      if (response.data.success) {
        return {
          success: true,
          message: response.data.message ?? 'Shell analysis completed successfully',
          data: response.data.data
        }
      } else {
        return {
          success: false,
          message: `[Frontend API] ${response.data.message ?? 'Shell analysis failed'}`
        }
      }
    } catch (error: any) {
      console.error('Shell analysis error:', error)
      return { 
        success: false, 
        message: `[Frontend API Error] ${error.response?.data?.message ?? error.message ?? 'Shell analysis failed'}` 
      }
    }
  },

  // glTF Export Methods
  exportFrameGLTF: async (model: FrameModel): Promise<Blob> => {
    try {
      console.log('Exporting frame to glTF:', model)
      const response = await api.post('/FrameAnalysis/export-gltf', model, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Frame glTF export error:', error)
      throw error
    }
  },

  exportShellGLTF: async (model: ShellModel): Promise<Blob> => {
    try {
      console.log('Exporting shell to glTF:', model)
      const response = await api.post('/ShellAnalysis/export-gltf', model, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Shell glTF export error:', error)
      throw error
    }
  },

}

// Examples API
export const examplesApi = {
  // Get example data
  getExamples: async (): Promise<ExampleData> => {
    try {
      const response = await api.get<ApiResponse<ExampleData>>('/examples')
      return response.data.data ?? { frameExamples: [], shellExamples: [] }
    } catch (error: any) {
      console.error('Failed to fetch examples:', error)
      return { frameExamples: [], shellExamples: [] }
    }
  },

  // Get frame examples
  getFrameExamples: async (): Promise<FrameModel[]> => {
    try {
      const response = await api.get<ApiResponse<FrameModel[]>>('/examples/frame')
      return response.data.data ?? []
    } catch (error: any) {
      console.error('Failed to fetch frame examples:', error)
      return []
    }
  },

  // Get shell examples
  getShellExamples: async (): Promise<ShellModel[]> => {
    try {
      const response = await api.get<ApiResponse<ShellModel[]>>('/examples/shell')
      return response.data.data ?? []
    } catch (error: any) {
      console.error('Failed to fetch shell examples:', error)
      return []
    }
  },

  // SMBeam Library Tests
  testSMBeamHealth: async (): Promise<any> => {
    const response = await api.get('/LibraryTest/health')
    return response.data
  },

  testSMBeamVersions: async (): Promise<any> => {
    const response = await api.get('/LibraryTest/versions')
    return response.data
  },

  testSMBeamBeam: async (): Promise<any> => {
    const response = await api.get('/LibraryTest/test-beam')
    return response.data
  },

  testSMBeamReport: async (): Promise<any> => {
    const response = await api.get('/LibraryTest/test-report')
    return response.data
  },

  testSMBeamCombined: async (): Promise<any> => {
    const response = await api.get('/LibraryTest/test-combined')
    return response.data
  },
}

// Download tracking API
export const downloadApi = {
  trackDownload: async (fileName: string, userInfo?: string): Promise<void> => {
    try {
      await api.post('/downloads/track', { fileName, userInfo })
    } catch (error: any) {
      console.error('Failed to track download:', error)
    }
  },
}

// Individual function exports for convenience
export const analyzeFrame = analysisApi.analyzeFrame
export const analyzeShell = analysisApi.analyzeShell

export const getFrameExamples = examplesApi.getFrameExamples
export const getShellExamples = examplesApi.getShellExamples

export default api

// Libraries admin API (dev-only usage)
export const librariesAdminApi = {
  importMaterials: async (): Promise<{ steelInserted: number, timberInserted: number }> => {
    const response = await api.post('/Libraries/materials/import')
    const data = response.data?.data ?? { steelInserted: 0, timberInserted: 0 }
    return data
  },
  initDbs: async (): Promise<{ codesSeeded: boolean, steelInserted: number, timberInserted: number }> => {
    const response = await api.post('/Libraries/init')
    return (response.data?.data ?? { codesSeeded: false, steelInserted: 0, timberInserted: 0 })
  }
}

// Libraries read API
export type SteelLibraryItem = {
  id: number
  grade: string
  subgrade: string
  productForm: string
  thicknessMax: number
  yieldStrength: number // MPa
  ultimateStrengthMin: number // MPa
  ultimateStrengthMax: number // MPa
}

export type TimberLibraryItem = {
  id: number
  kind: string // 'solid' | 'glulam'
  class: string
  fmK: number
  ft0K: number
  ft90K: number
  fc0K: number
  fc90K: number
  fvK: number
  frK: number
  e0Mean: number
  e005: number
  e90Mean: number
  gMean: number
  density: number
  gammaM: number
}

export const librariesApi = {
  getSteel: async (grade?: string, subgrade?: string): Promise<SteelLibraryItem[]> => {
    const params: any = {}
    if (grade) params.grade = grade
    if (subgrade) params.subgrade = subgrade
    const res = await api.get('/Libraries/materials/steel', { params })
    return (res.data?.data ?? []) as SteelLibraryItem[]
  },
  getSteelDesign: async (grade: string, thickness: number, subgrade?: string, productForm?: string, annex?: string): Promise<{ fyd: number; fud: number; gammaM0: number; gammaM2: number } > => {
    const params: Record<string, string | number> = { grade, thickness }
    if (subgrade) params.subgrade = subgrade
    if (productForm) params.productForm = productForm
    if (annex) params.annex = annex
    const res = await api.get('/Libraries/materials/steel/design', { params })
    const d = res.data?.data
    if (!d) {
      throw new Error(`Steel design lookup returned no data for grade='${grade}', thickness=${thickness}, subgrade='${subgrade ?? ''}', productForm='${productForm ?? ''}', annex='${annex ?? ''}'.`)
    }
    return d as { fyd: number; fud: number; gammaM0: number; gammaM2: number }
  },
  getTimber: async (kind?: 'solid' | 'glulam', timberClass?: string): Promise<TimberLibraryItem[]> => {
    const params: any = {}
    if (kind) params.kind = kind
    if (timberClass) params.class = timberClass
    const res = await api.get('/Libraries/materials/timber', { params })
    return (res.data?.data ?? []) as TimberLibraryItem[]
  },
  // kmod table for a service class, keyed by load-duration code (EN 1995-1-1 Table 3.1).
  // Values come from the backend (TimberCodeCheck) so the frontend never hardcodes them.
  getTimberKmodTable: async (serviceClass: 1 | 2 | 3): Promise<Record<string, number>> => {
    const res = await api.get('/Libraries/materials/timber/kmod', { params: { serviceClass } })
    const rows = (res.data?.data ?? []) as Array<{ duration: string; kmod: number }>
    return Object.fromEntries(rows.map(row => [row.duration, row.kmod]))
  }
}
